import { BaseRenderable, isTextNodeRenderable, TextNodeRenderable, TextRenderable, Yoga } from "@opentui/core";
function getLayoutNodeConstructor(parent) {
  const parentLayoutNode = parent?.getLayoutNode?.();
  return parentLayoutNode?.constructor;
}
function createLayoutSlotYogaNode(parentNodeConstructor) {
  return parentNodeConstructor?.create?.() ?? Yoga.default.Node.create();
}
class SlotBaseRenderable extends BaseRenderable {
  constructor(id) {
    super({
      id
    });
  }
  add(obj, index) {
    throw new Error("Can't add children on an Slot renderable");
  }
  getChildren() {
    return [];
  }
  remove(child) {}
  insertBefore(obj, anchor) {
    throw new Error("Can't add children on an Slot renderable");
  }
  getRenderable(id) {
    return undefined;
  }
  getChildrenCount() {
    return 0;
  }
  requestRender() {}
  findDescendantById(id) {
    return undefined;
  }
}
export class TextSlotRenderable extends TextNodeRenderable {
  destroyed = false;
  constructor(id, parent) {
    super({
      id: id
    });
    this._visible = false;
    this.slotParent = parent;
  }
  detachFromSlot() {
    this.slotParent = undefined;
  }
  disposeWithoutSlotCascade() {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.detachFromSlot();
  }
  destroy() {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    const slotParent = this.slotParent;
    this.slotParent = undefined;
    slotParent?.destroy();
    super.destroy();
  }
}
export class LayoutSlotRenderable extends SlotBaseRenderable {
  destroyed = false;
  yogaNodeFreed = false;
  constructor(id, parent, layoutParent) {
    super(id);
    this._visible = false;
    this.slotParent = parent;
    this.yogaNodeConstructor = getLayoutNodeConstructor(layoutParent);
    this.yogaNode = createLayoutSlotYogaNode(this.yogaNodeConstructor);
    this.yogaNode.setDisplay(Yoga.Display.None);
  }
  getLayoutNode() {
    return this.yogaNode;
  }
  updateFromLayout() {}
  updateLayout() {}
  onRemove() {}
  isCompatibleWith(layoutParent) {
    return this.yogaNodeConstructor === getLayoutNodeConstructor(layoutParent);
  }
  detachFromSlot() {
    this.slotParent = undefined;
  }
  freeYogaNode() {
    if (this.yogaNodeFreed) {
      return;
    }
    this.yogaNodeFreed = true;
    try {
      this.yogaNode.free();
    } catch {}
  }
  disposeWithoutSlotCascade() {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.detachFromSlot();
    this.freeYogaNode();
  }
  destroy() {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    const slotParent = this.slotParent;
    this.slotParent = undefined;
    this.freeYogaNode();
    slotParent?.destroy();
  }
}
export class SlotRenderable extends SlotBaseRenderable {
  destroyed = false;
  layoutNodesByParent = new Map();
  textNodesByParent = new Map();
  layoutNodeCount = 0;
  textNodeCount = 0;
  constructor(id) {
    super(id);
    this._visible = false;
  }
  get layoutNode() {
    return this.getCurrentSlotChild(this.layoutNodesByParent);
  }
  get textNode() {
    return this.getCurrentSlotChild(this.textNodesByParent);
  }
  isTextSlotParent(parent) {
    return isTextNodeRenderable(parent) || parent instanceof TextRenderable;
  }
  getCurrentSlotChild(nodesByParent) {
    for (const node of nodesByParent.values()) {
      if (node.parent) {
        return node;
      }
    }
    return nodesByParent.values().next().value;
  }
  getTextNodeForParent(parent) {
    const mappedNode = this.textNodesByParent.get(parent);
    if (mappedNode) {
      return mappedNode;
    }
    for (const [mappedParent, textNode] of this.textNodesByParent) {
      if (textNode.parent !== parent) {
        continue;
      }
      this.textNodesByParent.delete(mappedParent);
      this.textNodesByParent.set(parent, textNode);
      return textNode;
    }
  }
  getLayoutNodeForParent(parent) {
    const mappedNode = this.layoutNodesByParent.get(parent);
    if (mappedNode) {
      return mappedNode;
    }
    for (const [mappedParent, layoutNode] of this.layoutNodesByParent) {
      if (layoutNode.parent !== parent) {
        continue;
      }
      this.layoutNodesByParent.delete(mappedParent);
      this.layoutNodesByParent.set(parent, layoutNode);
      return layoutNode;
    }
  }
  takeReusableTextNode(parent) {
    for (const [mappedParent, textNode] of this.textNodesByParent) {
      if (textNode.parent) {
        continue;
      }
      this.textNodesByParent.delete(mappedParent);
      this.textNodesByParent.set(parent, textNode);
      return textNode;
    }
  }
  takeReusableLayoutNode(parent) {
    for (const [mappedParent, layoutNode] of this.layoutNodesByParent) {
      if (layoutNode.parent) {
        continue;
      }
      if (!layoutNode.isCompatibleWith(parent)) {
        continue;
      }
      this.layoutNodesByParent.delete(mappedParent);
      this.layoutNodesByParent.set(parent, layoutNode);
      return layoutNode;
    }
  }
  disposeDetachedTextNodes() {
    for (const [parent, textNode] of this.textNodesByParent) {
      if (textNode.parent) {
        continue;
      }
      this.textNodesByParent.delete(parent);
      textNode.disposeWithoutSlotCascade();
    }
  }
  disposeDetachedIncompatibleLayoutNodes(parent) {
    for (const [mappedParent, layoutNode] of this.layoutNodesByParent) {
      if (layoutNode.parent || layoutNode.isCompatibleWith(parent)) {
        continue;
      }
      this.layoutNodesByParent.delete(mappedParent);
      layoutNode.disposeWithoutSlotCascade();
    }
  }

  // A slot can have multiple placeholder children attached transiently while a
  // move is in flight. Portal host tracking relies on `slot.parent` pointing at
  // one of the still-live hosts, not necessarily the most recently inserted one.
  getAttachedSlotParent(excludedNode) {
    for (const textNode of this.textNodesByParent.values()) {
      if (textNode !== excludedNode && textNode.parent) {
        return textNode.parent;
      }
    }
    for (const layoutNode of this.layoutNodesByParent.values()) {
      if (layoutNode !== excludedNode && layoutNode.parent) {
        return layoutNode.parent;
      }
    }
    return null;
  }
  hasOtherAttachedSlotChildren(excludedNode) {
    return this.getAttachedSlotParent(excludedNode) !== null;
  }
  getSlotChild(parent) {
    if (this.isTextSlotParent(parent)) {
      const existingTextNode = this.getTextNodeForParent(parent);
      if (existingTextNode) {
        return existingTextNode;
      }
      const reusableTextNode = this.takeReusableTextNode(parent);
      if (reusableTextNode) {
        return reusableTextNode;
      }
      this.disposeDetachedIncompatibleLayoutNodes(parent);
      const textNode = new TextSlotRenderable(`slot-text-${this.id}-${++this.textNodeCount}`, this);
      this.textNodesByParent.set(parent, textNode);
      return textNode;
    }
    const existingLayoutNode = this.getLayoutNodeForParent(parent);
    if (existingLayoutNode) {
      return existingLayoutNode;
    }
    const reusableLayoutNode = this.takeReusableLayoutNode(parent);
    if (reusableLayoutNode) {
      return reusableLayoutNode;
    }
    this.disposeDetachedTextNodes();
    this.disposeDetachedIncompatibleLayoutNodes(parent);
    const layoutNode = new LayoutSlotRenderable(`slot-layout-${this.id}-${++this.layoutNodeCount}`, this, parent);
    this.layoutNodesByParent.set(parent, layoutNode);
    return layoutNode;
  }
  getSlotChildForRemoval(parent) {
    if (this.isTextSlotParent(parent)) {
      return this.getTextNodeForParent(parent);
    }
    return this.getLayoutNodeForParent(parent);
  }
  didRemoveSlotChild(parent, child) {
    const hasOtherAttachedSlotChildren = this.hasOtherAttachedSlotChildren(child);
    if (hasOtherAttachedSlotChildren && child instanceof TextSlotRenderable && this.getTextNodeForParent(parent) === child) {
      this.textNodesByParent.delete(parent);
      child.disposeWithoutSlotCascade();
    }
    if (hasOtherAttachedSlotChildren && child instanceof LayoutSlotRenderable && this.getLayoutNodeForParent(parent) === child) {
      this.layoutNodesByParent.delete(parent);
      child.disposeWithoutSlotCascade();
    }
    if (this.parent === parent) {
      this.parent = this.getAttachedSlotParent(child);
    }
  }
  destroy() {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    const layoutNodes = new Set(this.layoutNodesByParent.values());
    this.layoutNodesByParent.clear();
    for (const layoutNode of layoutNodes) {
      layoutNode.destroy();
    }
    const textNodes = new Set(this.textNodesByParent.values());
    this.textNodesByParent.clear();
    for (const textNode of textNodes) {
      textNode.destroy();
    }
  }
}